import type {
    IWasteMovementDefaultsPageModel,
    IWasteMovementDefaultsResolvedModel,
    IWasteMovementDefaultFieldSaveItem,
    IWasteMovementEwcRuleSavePayload,
    IWasteMovementMostFrequentPreview,
} from "@/customTypings/wasteMovement/WasteMovementDefaults";
import { authHeader, handleResponse } from "../../utils";

function base(siteId: string) {
    return `/api/sites/${encodeURIComponent(siteId)}/wastemovementdefaults`;
}

export const wasteMovementDefaultsService = {
    getPage,
    getResolved,
    getMostFrequentPreview,
    saveGeneral,
    createEwcRule,
    updateEwcRule,
    enableEwcRule,
    disableEwcRule,
    softDeleteEwcRule,
    restoreEwcRule,
};

function getPage(siteId: string, movementDirection: string) {
    return fetch(`${base(siteId)}/${encodeURIComponent(movementDirection)}`, {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    }).then(handleResponse) as Promise<IWasteMovementDefaultsPageModel>;
}

function getResolved(siteId: string, movementDirection: string) {
    return fetch(`${base(siteId)}/${encodeURIComponent(movementDirection)}/resolved`, {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    }).then(handleResponse) as Promise<IWasteMovementDefaultsResolvedModel>;
}

function getMostFrequentPreview(siteId: string, movementDirection: string, fieldKey: string) {
    return fetch(
        `${base(siteId)}/${encodeURIComponent(movementDirection)}/most-frequent/${encodeURIComponent(fieldKey)}`,
        {
            method: "GET",
            headers: authHeader(),
            credentials: "include",
        }
    ).then(handleResponse) as Promise<IWasteMovementMostFrequentPreview>;
}

function saveGeneral(siteId: string, movementDirection: string, fields: IWasteMovementDefaultFieldSaveItem[]) {
    return fetch(`${base(siteId)}/${encodeURIComponent(movementDirection)}/general/save`, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify({ movementDirection, fields }),
    }).then(handleResponse) as Promise<IWasteMovementDefaultsPageModel>;
}

function createEwcRule(siteId: string, payload: IWasteMovementEwcRuleSavePayload) {
    return fetch(`${base(siteId)}/ewc-rules/create`, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    }).then(handleResponse) as Promise<IWasteMovementDefaultsPageModel>;
}

function updateEwcRule(siteId: string, payload: IWasteMovementEwcRuleSavePayload) {
    return fetch(`${base(siteId)}/ewc-rules/update`, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    }).then(handleResponse) as Promise<IWasteMovementDefaultsPageModel>;
}

function enableEwcRule(siteId: string, ruleId: string) {
    return postRuleLifecycle(siteId, "enable", ruleId);
}

function disableEwcRule(siteId: string, ruleId: string) {
    return postRuleLifecycle(siteId, "disable", ruleId);
}

function softDeleteEwcRule(siteId: string, ruleId: string) {
    return postRuleLifecycle(siteId, "softdelete", ruleId);
}

function restoreEwcRule(siteId: string, ruleId: string) {
    return postRuleLifecycle(siteId, "restore", ruleId);
}

function postRuleLifecycle(siteId: string, action: string, ruleId: string) {
    return fetch(`${base(siteId)}/ewc-rules/${action}?id=${encodeURIComponent(ruleId)}`, {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    }).then(handleResponse) as Promise<IWasteMovementDefaultsPageModel>;
}
